import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCashflows, useDeleteCashflow } from "../../hooks/investments";
import type { CashflowDto } from "../../lib/investments";
import { CashflowFormModal } from "./CashflowFormModal";

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

interface Props {
  investmentId: string;
}

export function CashflowTable({ investmentId }: Props) {
  const { t } = useTranslation();
  const { data, isLoading } = useCashflows(investmentId, true);
  const deleteMutation = useDeleteCashflow(investmentId);
  const [editingCashflow, setEditingCashflow] = useState<CashflowDto | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  if (isLoading) return <p className="text-sm text-slate-500">...</p>;

  const cashflows = data?.cashflows ?? [];

  if (cashflows.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-4 text-center">
        {t("investments.cashflow.no_cashflows")}
      </p>
    );
  }

  async function handleDelete(id: string) {
    await deleteMutation.mutateAsync(id);
    setConfirmDeleteId(null);
  }

  return (
    <>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left border-b">
            <th className="p-2">{t("investments.cashflow.header_date")}</th>
            <th className="p-2">{t("investments.cashflow.header_type")}</th>
            <th className="p-2 text-right">{t("investments.cashflow.header_amount")}</th>
            <th className="p-2">{t("investments.cashflow.header_source")}</th>
            <th className="p-2">{t("investments.cashflow.header_notes")}</th>
            <th scope="col" className="p-2">
              <span className="sr-only">{t("investments.cashflow.header_actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {[...cashflows]
            .sort((a, b) => b.date.localeCompare(a.date))
            .map((cf) => (
              <tr key={cf.id} className="border-b">
                <td className="p-2 font-mono text-xs">{cf.date}</td>
                <td className="p-2">
                  <span
                    className={`px-2 py-0.5 text-xs rounded ${
                      cf.type === "DEPOSIT"
                        ? "bg-green-100 text-green-700"
                        : cf.type === "WITHDRAW"
                          ? "bg-red-100 text-red-700"
                          : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {t(`investments.cashflow.type_${cf.type}`)}
                  </span>
                </td>
                <td className="p-2 text-right font-mono">{formatEur(cf.amountCents)}</td>
                <td className="p-2">
                  <span
                    className={`px-2 py-0.5 text-xs rounded ${
                      cf.source === "IMPORT"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {t(`investments.cashflow.source_${cf.source}`)}
                  </span>
                </td>
                <td className="p-2 text-xs text-slate-600">{cf.notes ?? "—"}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button
                    type="button"
                    className="text-xs text-blue-600 underline mr-2"
                    onClick={() => setEditingCashflow(cf)}
                  >
                    {t("investments.cashflow.edit_button")}
                  </button>
                  {confirmDeleteId === cf.id ? (
                    <>
                      <button
                        type="button"
                        className="text-xs text-red-700 underline mr-1"
                        onClick={() => handleDelete(cf.id)}
                        disabled={deleteMutation.isPending}
                      >
                        {t("investments.cashflow.delete_confirm")}
                      </button>
                      <button
                        type="button"
                        className="text-xs text-slate-500 underline"
                        onClick={() => setConfirmDeleteId(null)}
                      >
                        {t("common.cancel")}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="text-xs text-red-700"
                      onClick={() => setConfirmDeleteId(cf.id)}
                    >
                      {t("investments.cashflow.delete_button")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {editingCashflow && (
        <CashflowFormModal
          investmentId={investmentId}
          existing={editingCashflow}
          onClose={() => setEditingCashflow(null)}
        />
      )}
    </>
  );
}
